/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { inject } from "@angular/core";
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { first, map, Observable, of, Subject, switchMap, tap } from "rxjs";
import { AccountStatus, UserAuth } from "../concept/user";
import { LOGOUT_REASON, TEAM_ACTION, RETURN_TO } from "../framework/url-params";
import { ApplicationState } from "../service/application-state.service";
import { UserController } from "../service/user/user-controller.service";
import { TeamController } from "../service/team/team-controller.service";
import { TeamApi } from "../service/team/team-api.service";
import { Pagination, Parameters, Sorting } from "../concept/common";

type AuthGuardContext = {
    app: ApplicationState,
    router: Router,
    routerState: RouterStateSnapshot,
    userCtl: UserController,
    teamApi: TeamApi,
};

function createAuthGuardContext(routerState: RouterStateSnapshot): AuthGuardContext {
    return {
        app: inject(ApplicationState),
        router: inject(Router),
        routerState: routerState,
        userCtl: inject(UserController),
        teamApi: inject(TeamApi),
    };
}

export const genericGuard: CanActivateFn = (_route, state) => {
    const ctx = createAuthGuardContext(state);
    return ctx.app.userAuth$.pipe(first(), switchMap((user) => resolveGeneric(user, ctx)));
};

const resolveExpectedAuthState: (validStates: (AccountStatus | "logged_out")[], ctx: AuthGuardContext) => Observable<boolean | UrlTree> = (validStates, ctx) => {
    return ctx.app.userAuth$.pipe(
        first(),
        switchMap((userAuth) => {
            if (validStates.includes(userAuth.status)) return of(true);
            else return resolveGeneric(userAuth, ctx);
        }),
    );
}

export const loggedOutGuard: CanActivateFn = (_route, state) => resolveExpectedAuthState(["logged_out"], createAuthGuardContext(state));

export const setupCompletedGuard: CanActivateFn = (_route, state) => resolveExpectedAuthState(["setup_completed"], createAuthGuardContext(state));

export const mfaGuard: CanActivateFn = (_route, state) => resolveExpectedAuthState(["verified"], createAuthGuardContext(state))

export const setupGuard: CanActivateFn = (_route, state) => resolveExpectedAuthState(["verified", "profile_created", "survey_completed", "team_joined_not_surveyed"], createAuthGuardContext(state))

export const teamJoinedNotSurveyedGuard: CanActivateFn = (_route, state) => resolveExpectedAuthState(["team_joined_not_surveyed"], createAuthGuardContext(state));

function resolveGeneric(userAuth: UserAuth, ctx: AuthGuardContext): Observable<UrlTree | boolean> {
    switch (userAuth.status) {
        case "logged_out": {
            if (hasUrlComponents(ctx.routerState)) return of(ctx.router.parseUrl(`sign-in?${RETURN_TO}=${ctx.routerState.url}`));
            else return of(ctx.router.parseUrl("sign-in"));
        }
        case "unverified": {
            return of(ctx.router.parseUrl("verify-email"));
        }
        case "verified": {
            return of(ctx.router.parseUrl("mfa"));
        }
        case "profile_created":
        case "survey_completed": {
            return of(ctx.router.parseUrl("setup"));
        }
        case "setup_completed": {
            const teamAction = ctx.routerState.root.queryParamMap.get(TEAM_ACTION) ?? ctx.routerState.root.queryParamMap.get("org_action") ?? "";
            return ctx.userCtl.getDefaultTeam().pipe(
                map(team => {
                    let url = ctx.router.parseUrl(`team/${team.id}${teamAction}`);
                    url.fragment = ctx.routerState.root.fragment;
                    return url;
                }),
            );
        }
        case "team_joined_not_surveyed": {
            if (hasUrlComponents(ctx.routerState)) return of(ctx.router.parseUrl(`setup/project?${RETURN_TO}=${ctx.routerState.url}`));
            else return of(ctx.router.parseUrl(`setup/project`));
        }
        // TODO: invalid status handling
        default: {
            return ctx.app.signOut({ deauthUser: true, redirect: false, announce: false }).pipe(
                map(() => {
                    if (hasUrlComponents(ctx.routerState)) return ctx.router.parseUrl(`sign-in?${RETURN_TO}=${ctx.routerState.url}&${LOGOUT_REASON}=invalid_token`);
                    else return ctx.router.parseUrl(`sign-in?${LOGOUT_REASON}=invalid_token`);
                }),
                tap(() => ctx.app.unsetCurrentTeam()),
            );
        }
    }
}

function hasUrlComponents(routerState: RouterStateSnapshot): boolean {
    return routerState.url.length > 1;
}

export const getStartedOverviewGuard: CanActivateFn = (route, state) => {
    const ctx = createAuthGuardContext(state);
    const teamID: string = route.params['team-id'];
    const pagination: Pagination = { offset: 0, limit: 1 };
    const sorting: Sorting = { attributeType: "id", direction: "asc" };
    const params: Parameters = { pagination, sorting, filters: [] };
    return ctx.teamApi.getTeam(teamID, of(undefined)).pipe(switchMap((team) =>
        ctx.teamApi.listClusters(team.initial!.uuid, params, of(undefined)).pipe(map((items) => {
            if (items.initial!.length > 0) {
                return ctx.router.parseUrl(`team/${teamID}/overview`);
            } else {
                return ctx.router.parseUrl(`team/${teamID}/get-started`);
            }
        }))
    ));
}
